import { Box, Paper, Typography, Skeleton } from '@mui/material'
import { useEffect, useState } from 'react'
import axios from 'src/utilities/http-client'
import dayjs from 'dayjs'
import {
  CourseAnalyticsRequest,
  LikertDataResponse,
  LearnerCountsResponse,
} from '../scribe.slice'
import LearnerCountsChart from './learner-counts-chart'
import LikertTimeSeriesChart from './likert-time-series-chart'

interface AnalyticsChartsProps {
  course: any
  licenseType: string | null
  startDate: string | null
  endDate: string | null
}

export default function AnalyticsCharts({
  course,
  licenseType,
  startDate,
  endDate,
}: AnalyticsChartsProps) {
  let [likertData, setLikertData] = useState<LikertDataResponse>([])
  let [learnerCounts, setLearnerCounts] = useState<LearnerCountsResponse>([])
  let [likertLoading, setLikertLoading] = useState(false)
  let [learnerCountsLoading, setLearnerCountsLoading] = useState(false)
  let [error, setError] = useState<string | null>(null)

  let canFetchData = course && licenseType && startDate && endDate

  let fetchAnalyticsData = async () => {
    if (!canFetchData) return

    let request: CourseAnalyticsRequest = {
      courseId: course.id,
      licenseGroup: licenseType as any,
      startDate: dayjs(startDate).startOf('day').toISOString(),
      endDate: dayjs(endDate).endOf('day').toISOString(),
    }

    try {
      setError(null)

      // Fetch both datasets in parallel
      setLikertLoading(true)
      setLearnerCountsLoading(true)

      let [likertResponse, learnerResponse] = await Promise.all([
        axios.post<LikertDataResponse>(
          '/api/scribe/courses/likert-data',
          request,
        ),
        axios.post<LearnerCountsResponse>(
          '/api/scribe/courses/learner-counts',
          request,
        ),
      ])

      setLikertData(likertResponse.data)
      setLearnerCounts(learnerResponse.data)
    } catch (err: any) {
      let errorMsg =
        err.response?.data?.error || 'Failed to fetch analytics data'
      setError(errorMsg)
    } finally {
      setLikertLoading(false)
      setLearnerCountsLoading(false)
    }
  }

  // Clear data when filters change
  useEffect(() => {
    setLikertData([])
    setLearnerCounts([])
    setError(null)
  }, [course?.id, licenseType, startDate, endDate])

  // Fetch data when filters are complete
  useEffect(() => {
    if (canFetchData) {
      fetchAnalyticsData()
    }
  }, [course?.id, licenseType, startDate, endDate])

  if (!canFetchData) {
    return (
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Analytics Dashboard
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Please select a course, license type, and date range to view
          analytics.
        </Typography>
      </Paper>
    )
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Analytics Dashboard
        </Typography>
        <Typography color="error" variant="body1">
          Error: {error}
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Analytics Dashboard
      </Typography>
      <Box className="space-y-6">
        {/* Likert Analysis Chart - Full Width on Top */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Survey Satisfaction Trends
          </Typography>
          {likertLoading ? (
            <Box
              sx={{ bgcolor: 'background.paperDark', borderRadius: 1, p: 2 }}
            >
              <Skeleton
                variant="rectangular"
                sx={{
                  height: 300,
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{ bgcolor: 'background.paperDark', borderRadius: 1, p: 2 }}
            >
              <LikertTimeSeriesChart data={likertData} course={course} />
            </Box>
          )}
        </Box>

        {/* Learner Counts Chart - Full Width Below */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Learner Participation Over Time
          </Typography>
          {learnerCountsLoading ? (
            <Box
              sx={{ bgcolor: 'background.paperDark', borderRadius: 1, p: 2 }}
            >
              <Skeleton
                variant="rectangular"
                sx={{
                  height: 300,
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{ bgcolor: 'background.paperDark', borderRadius: 1, p: 2 }}
            >
              <LearnerCountsChart data={learnerCounts} course={course} />
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  )
}
